import cn from "@/v2-ui/utils/utils.cn"
import Tooltip from "@/v2-ui/tooltip"

export type MigrationRollup = {
  MigrationTotal?: number | null
  MigrationActivated?: number | null
  MigrationPending?: number | null
  MigrationExpired?: number | null
  MigrationBlocked?: number | null
  /** Members with v2 evidence — the wave's target group (informational). */
  MigrationV2Users?: number | null
}

export type MigrationProgressProps = {
  rollup: MigrationRollup
  /** sm = table cell, lg = panel/dialog hero. */
  size?: "sm" | "lg"
  className?: string
}

type Segment = {
  key: string
  label: string
  count: number
  barClass: string
  dotClass: string
}

/**
 * Derives the display segments from a Fusion-migration rollup (FUS-927).
 * Buckets mirror the migration board's per-user statuses; "not migrated"
 * is the remainder the SQL rollup leaves implicit.
 */
export function deriveMigrationSegments(rollup: MigrationRollup): {
  total: number
  activated: number
  segments: Segment[]
} {
  const total = rollup.MigrationTotal ?? 0
  const activated = rollup.MigrationActivated ?? 0
  const pending = rollup.MigrationPending ?? 0
  const expired = rollup.MigrationExpired ?? 0
  const blocked = rollup.MigrationBlocked ?? 0
  const notMigrated = Math.max(
    total - activated - pending - expired - blocked,
    0,
  )
  return {
    total,
    activated,
    segments: [
      {
        key: "activated",
        label: "Activated",
        count: activated,
        barClass: "bg-primary",
        dotClass: "bg-primary",
      },
      {
        key: "pending",
        label: "Link sent",
        count: pending,
        barClass: "bg-amber-500",
        dotClass: "bg-amber-500",
      },
      {
        key: "expired",
        label: "Link expired",
        count: expired,
        barClass: "bg-orange-700",
        dotClass: "bg-orange-700",
      },
      {
        key: "not-migrated",
        label: "Not migrated",
        count: notMigrated,
        barClass: "bg-muted-foreground/30",
        dotClass: "bg-muted-foreground/40",
      },
      {
        key: "blocked",
        label: "Blocked",
        count: blocked,
        barClass: "bg-destructive/50",
        dotClass: "bg-destructive/60",
      },
    ],
  }
}

/**
 * The "Login Migration" progress bar (FUS-927): one stacked bar over a
 * scope's members — activated first, then pending/expired links, the
 * not-migrated remainder, and blocked members. Shared by the team and
 * customer tables, both panels, and the Migration Center dialog; the
 * tooltip carries the per-bucket breakdown.
 */
function MigrationProgress(props: MigrationProgressProps) {
  const { rollup, size = "sm", className } = props
  const { total, activated, segments } = deriveMigrationSegments(rollup)

  if (!total) {
    return (
      <span className={cn("text-xs text-muted-foreground", className)}>
        No members
      </span>
    )
  }

  const bar = (
    <div
      className={cn(
        "flex w-full items-center gap-2",
        size === "lg" && "gap-3",
        className,
      )}
    >
      <div
        role="img"
        aria-label={`${activated} of ${total} members activated`}
        className={cn(
          "flex flex-1 overflow-hidden rounded-full bg-muted",
          size === "sm" ? "h-1.5" : "h-3",
        )}
      >
        {segments
          .filter((s) => s.count > 0)
          .map((s) => (
            <div
              key={s.key}
              className={s.barClass}
              style={{ width: `${(s.count / total) * 100}%` }}
            />
          ))}
      </div>
      <span
        className={cn(
          "shrink-0 tabular-nums text-muted-foreground",
          size === "sm" ? "text-xs" : "text-sm font-medium",
        )}
      >
        {activated}/{total}
      </span>
    </div>
  )

  return (
    <Tooltip>
      <Tooltip.Trigger asChild>{bar}</Tooltip.Trigger>
      <Tooltip.Content side="top" className="ui-tooltip-content--panel">
        <div className="flex min-w-44 flex-col gap-1.5 p-3">
          <span className="font-medium text-popover-foreground">
            Fusion login migration
          </span>
          {segments.map((s) => (
            <span
              key={s.key}
              className="flex items-center gap-2 text-muted-foreground"
            >
              <span
                aria-hidden
                className={cn("size-2 shrink-0 rounded-full", s.dotClass)}
              />
              <span className="flex-1">{s.label}</span>
              <span className="tabular-nums">{s.count}</span>
            </span>
          ))}
          {rollup.MigrationV2Users != null && (
            <span className="mt-1 flex items-center gap-2 border-t border-border pt-1.5 text-muted-foreground">
              <span className="flex-1">V2 users (target group)</span>
              <span className="tabular-nums">{rollup.MigrationV2Users}</span>
            </span>
          )}
        </div>
      </Tooltip.Content>
    </Tooltip>
  )
}

export default MigrationProgress
